import { useEffect, useState } from "react";
import { FaArrowRightLong } from "react-icons/fa6";
import banner from "../../../Assets/Home/HeroBanner.png";
import HeroVideo from "./HeroVideo";
import { useNavigate } from "react-router-dom";
import api from "../../../configs/api";

const Avatar = ({ initials, color }) => (
  <div
    className="w-7 h-7 sm:w-9  sm:h-9 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-bold -ml-2 first:ml-0"
    style={{ backgroundColor: color }}
  >
    {initials}
  </div>
);

const stats = [
  {
    title1: "Professional Experts",
    title2: "Worker",
    value: "213",
  },
  {
    title1: "Successful Projects",
    title2: "Completed",
    value: "5400",
  },
];

const useCountUp = (end, duration = 1500) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const incrementTime = 20;
    const steps = duration / incrementTime;
    const increment = end / steps;

    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, incrementTime);

    return () => clearInterval(timer);
  }, [end, duration]);

  return count;
};

const StatItem = ({ stat, animated, index }) => {
  const value = useCountUp(Number(stat.value), 1500);

  return (
    <div
      className="flex relative items-center bg-gradient-to-r from-transparent via-black/10 to-black/15 gap-3 lg:gap-4 justify-between sm:justify-end flex-1 lg:flex-none"
      style={{
        opacity: animated ? 1 : 0,
        transform: animated ? "translateX(0)" : "translateX(30px)",
        transition: `opacity 0.6s ease ${0.3 + index * 0.1}s, transform 0.6s ease ${
          0.3 + index * 0.1
        }s`,
      }}
    >
      <div className="text-left sm:text-right tracking-wide text-[#DDDDE6] text-sm lg:text-[1.1rem] leading-tight">
        <p>{stat.title1}</p>
        <p>{stat.title2}</p>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center font-bebas tracking-wider text-[2.5rem] sm:text-[3rem] lg:text-[3.3rem] gap-1 text-white font-extrabold leading-none">
          <span>{value}</span>
          <span>+</span>
        </div>

        <div className="w-[0.25rem] lg:w-[0.375rem] h-[3.125rem] lg:h-[5rem] bg-gradient-to-b from-primaryAccent to-primaryAccentHover rounded-full" />
      </div>
    </div>
  );
};

export default function MixMastersHero() {
  const [animated, setAnimated] = useState(false);
  const [reviews, setReviews] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await api.get("/reviews");

        let reviewList = [];

        if (response.data?.data) {
          reviewList = Array.isArray(response.data.data)
            ? response.data.data
            : response.data.data?.reviews || [];
        } else if (Array.isArray(response.data)) {
          reviewList = response.data;
        } else if (response.data?.reviews) {
          reviewList = response.data.reviews;
        }

        const activeReviews = reviewList.filter(
          (review) => review.status === "active",
        );

        setReviews(activeReviews);
      } catch (error) {
        console.error("Failed to fetch reviews", error);
      }
    };

    fetchReviews();
  }, []);

  return (
    <div className="p-3 sm:px-6 lg:px-10 sm:pt-4 sm:pb-8 lg:pb-10">
      <div
        className="relative w-full overflow-hidden rounded-xl"
        style={{
          minHeight: "500px",
          backgroundImage: `url(${banner})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Dark overlay */}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "linear-gradient(135deg, #1a1f3a 0%, #1e2340 40%, #1a2040 70%, #1e1a38 100%)",
          }}
        />

        {/* Grid */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />

        {/* Main */}
        <div className="relative z-10 min-h-[39rem] flex flex-col lg:flex-row lg:justify-between p-6 md:p-8 lg:p-10">
          {/* LEFT */}
          <div className="flex flex-col justify-between w-full lg:max-w-[70%]">
            <div>
              {/* Heading */}
              <h1
                className="uppercase text-white font-heading font-bold sm:font-semibold text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-wide sm:tracking-[0.2rem] drop-shadow-[0_2px_20px_rgba(0,0,0,0.4)] transition-[opacity,transform] duration-700 ease-in-out flex flex-col gap-2 lg:gap-6 py-4 lg:py-10"
                style={{
                  opacity: animated ? 1 : 0,
                  transform: animated ? "translateX(0)" : "translateX(-30px)",
                }}
              >
                <span className="leading-tight sm:leading-none">
                  Revolutionizing READY{" "}
                </span>
                <span className="leading-tight sm:leading-none">
                  MIX CONCRETE with
                </span>
                <span className="leading-tight sm:leading-none">
                  Innovation & Quality{" "}
                </span>
              </h1>

              {/* Description */}
              <p
                className="text-gray-300 text-xs sm:text-sm md:text-base lg:text-[1.1rem] font-body tracking-wide font-normal leading-relaxed max-w-full lg:max-w-[700px] pb-6 md:pb-8 lg:pb-10"
                style={{
                  opacity: animated ? 1 : 0,
                  transition: "opacity 0.7s ease 0.2s",
                }}
              >
                Mix Masters is a new-age ready-mix concrete company built by
                professionals with deep domain experience and a strong execution
                mindset.
              </p>
            </div>

            {/* CTA */}
            <div
              className="flex flex-wrap sm:flex-nowrap justify-between sm:justify-start gap-2 sm:gap-4 items-center max-w-full lg:max-w-[70%]"
              style={{
                opacity: animated ? 1 : 0,
                transition: "opacity 0.6s ease 0.35s",
              }}
            >
              <button
                type="button"
                onClick={() => navigate(`/products`)}
                className="group flex items-center text-sm gap-2 sm:gap-3 pl-3 pr-1.5 py-2 rounded-lg text-white font-body font-normal bg-primaryAccent transition-all transform-gpu duration-200 hover:bg-primaryAccentHover active:scale-[0.98]"
              >
                Explore Products
                <span className="bg-[#082344] p-2 rounded-lg transition-all duration-200 group-hover:translate-x-1 transform-gpu">
                  <FaArrowRightLong className="size-4" />
                </span>
              </button>

              <div
                className="flex items-center gap-3"
                style={{
                  opacity: animated ? 1 : 0,
                  transition: "opacity 0.6s ease 0.45s",
                }}
              >
                <div
                  className="flex items-center cursor-pointer"
                  onClick={() => {
                    const section = document.getElementById("reviews-section");

                    if (section) {
                      section.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }
                  }}
                >
                  {reviews.slice(0, 3).map((review, index) => {
                    const colors = ["#e040fb", "#7c4dff", "#26c6da"];

                    return (
                      <Avatar
                        key={review._id || index}
                        initials={review.name
                          ?.split(" ")
                          .map((word) => word[0])
                          .join("")
                          .slice(0, 2)
                          .toUpperCase()}
                        color={colors[index % colors.length]}
                      />
                    );
                  })}
                </div>
                <div className="font-normal font-body tracking-wider">
                  <span className="text-white font-bold text-sm">98%</span>
                  <span className="text-gray-300 text-sm ml-1">Client</span>
                  <br />
                  <span className="text-gray-300 text-xs">Satisfactions</span>
                </div>
              </div>
            </div>

            {/* Tagline */}
            <p
              className="text-gray-300 font-semibold tracking-wider font-body text-sm sm:text-lg mt-8"
              style={{
                opacity: animated ? 1 : 0,
                transition: "opacity 0.6s ease 0.55s",
              }}
            >
              Delivering Precision, Innovation, and Sustainability
            </p>
          </div>

          {/* RIGHT */}
          <div
            className="flex flex-col items-start lg:items-end justify-between gap-5 w-full pt-10 lg:w-auto"
            style={{
              opacity: animated ? 1 : 0,
              transform: animated ? "translateX(0)" : "translateX(30px)",
              transition: "opacity 0.7s ease 0.25s, transform 0.7s ease 0.25s",
            }}
          >
            {/* Stats */}
            <div className="flex flex-col gap-2 md:gap-4 lg:gap-8 w-full lg:w-auto">
              {stats.map((stat, index) => (
                <StatItem
                  key={index}
                  stat={stat}
                  animated={animated}
                  index={index}
                />
              ))}
            </div>

            {/* Video */}
            <HeroVideo />
          </div>
        </div>
      </div>
    </div>
  );
}
